import { CommonModule, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  input,
  Input,
  Output,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { SelectAllComponent } from './select-all/select-all.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatIcon,
    ReactiveFormsModule,
    SelectAllComponent,
    MatTooltipModule,
    MatIconModule,
    FormsModule
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
    {
      provide: MatFormFieldControl,
      useExisting: SelectComponent,
    },
  ],
})
export class SelectComponent implements OnInit, OnDestroy {
  @Input() formControl!: FormControl;
  options = input<any[]>([]);
  displayKey = input<string>('name');
  valueKey = input<string>('id');
  placeholder = input<string>('Select an option');
  defaultOption = input<any>(null);
  showAddView = input<boolean>(false);
  showEditView = input<boolean>(false);
  showTrash = input<boolean>(false);
  multiple = input<boolean>(false);
  viewTip = input<string>('edit');
  addTip = input<string>('add');
  showFavorite = input<boolean>(false);
  groupSelection = input<boolean>(false);
  returnFullObject = input<boolean>(false);
  showSelectAll =  input<boolean>(true);
  hideSubscript = input<boolean>(false)


  @Output() selectionChange = new EventEmitter<any>();
  @Output() removeSelect = new EventEmitter<any>();
  @Output() addRecord = new EventEmitter<any>();
  @Output() viewRecord = new EventEmitter<any>();
  @Output() facorite = new EventEmitter<any>();
  @Output() selectAll = new EventEmitter<any>();

  selectedOption: any = null;
  selectedValues: any[] = [];
  isDisabled = false;
  records = [];
  private valueChangesSub?: Subscription;
  filteredRecords: any[] = []; // Initially copy of records
  expandedGroups: {[key: string]: boolean} = {};

  searchQuery: string = '';

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      
      this.records = this.options();
      
      if(!this.records)
        return
      this.filteredRecords = this.records;
      if (this.formControl.value  && this.formControl.value !== "[object Object]") {
        this.selectedOption = this.formControl.value;
      }else if (this.defaultOption()){
        if (this.groupSelection() && this.records) {
          this.records.forEach(group => {
            this.expandedGroups[group.group] = true; // Default to expanded
          });
        }
        this.selectedOption = this.defaultOption()?.toString()
      }
      this.cdr.detectChanges();
    });
  }

  toggleCheckbox(event){
    this.selectAll.emit(event)
  }

  ngOnInit() {
    this.records = this.options();
    this.filteredRecords = this.records;
    if (!this.formControl) {
      this.formControl = new FormControl(null);
    }

    // Initialize with form control value or default option
    const initialValue = this.formControl.value || this.defaultOption();
    if (this.multiple()) {
      this.selectedValues = Array.isArray(initialValue) ? initialValue.map(row => row.toString()) : [];
    } else {
      this.selectedOption = initialValue && initialValue.toString();
    }

    // Subscribe to form control changes
    this.valueChangesSub = this.formControl.valueChanges.subscribe((value) => {
      if (this.multiple()) {
        this.selectedValues = Array.isArray(value) ? value : [];
      } else {
        this.selectedOption = value;
      }
      this.cdr.detectChanges();
    });

    if (this.groupSelection() && this.records) {
      this.records.forEach(group => {
        this.expandedGroups[group.group] = true; // Default to expanded
      });
    }
    // Add this at the end of ngOnInit
    if (this.groupSelection()) {
      this.findSelectedOptionInGroups();
    }
  }

  // Add this method after ngOnInit
findSelectedOptionInGroups() {
  if (!this.groupSelection() || !this.defaultOption() || !this.records) {
    return;
  }
  
  // Iterate through groups to find the matching item
  for (const group of this.records) {
    for (const item of group?.items) {
      if (item[this.valueKey()] == this.defaultOption()) {
        this.selectedOption = item[this.valueKey()].toString();
        // Ensure the group is expanded
        this.expandedGroups[group.group] = true;
        this.cdr.detectChanges();
        return;
      }
    }
  }
}

  toggleGroup(groupName: string): void {
    // Use a separate object to track expanded state
    this.expandedGroups[groupName] = !this.expandedGroups[groupName];
    this.cdr.detectChanges();
  }

  /** Is this item the current single-selection? */
private isSelected(item: any): boolean {
  return (
    !this.multiple() &&
    item[this.valueKey()]?.toString() === this.selectedOption
  );
}

/** Is this item in the multi-selection array? */
private isMultiSelected(item: any): boolean {
  return (
    this.multiple() &&
    this.selectedValues?.includes(item[this.valueKey()]?.toString())
  );
}

filterRecords() {
  const q = this.searchQuery.toLowerCase();

  // ---------- FLAT LIST ----------
  if (!this.groupSelection()) {
    const selectedItems: any[] = [];
    const unselectedItems: any[] = [];
    
    // Separate selected and unselected
    this.records.forEach((item) => {
      if (this.isSelected(item) || this.isMultiSelected(item)) {
        selectedItems.push(item);
      } else {
        unselectedItems.push(item);
      }
    });

    // Filter unselected items based on search (or show all if no search)
    const filteredUnselected = q 
      ? unselectedItems.filter(item => item[this.displayKey()].toLowerCase().includes(q))
      : unselectedItems;
    
    // Always show all selected items at the bottom
    this.filteredRecords = [...filteredUnselected, ...selectedItems];
    return;
  }

  // ---------- GROUPED LIST ----------
  const allSelectedItems: any[] = [];
  const filteredGroups: any[] = [];

  // Process each group
  this.records.forEach((group) => {
    const selectedInGroup: any[] = [];
    const unselectedInGroup: any[] = [];
    
    // Separate items in this group
    group.items?.forEach((item: any) => {
      if (this.isSelected(item) || this.isMultiSelected(item)) {
        selectedInGroup.push(item);
        allSelectedItems.push(item);
      } else {
        unselectedInGroup.push(item);
      }
    });
    
    // Filter unselected items in group (or show all if no search)
    const filteredUnselected = q
      ? unselectedInGroup.filter(item => item[this.displayKey()].toLowerCase().includes(q))
      : unselectedInGroup;
    
    // Only add group if it has unselected items to show
    if (filteredUnselected.length > 0) {
      filteredGroups.push({
        ...group,
        items: filteredUnselected
      });
    }
  });

  // Add all groups with unselected items
  this.filteredRecords = [...filteredGroups];
  
  // Always add selected items group at the end
  if (allSelectedItems.length > 0) {
    this.filteredRecords.push({
      group: 'Selected Items',
      items: allSelectedItems
    });
    this.expandedGroups['Selected Items'] = true;
  }

  // Ensure all groups are expanded
  this.filteredRecords.forEach((g) => {
    if (!(g.group in this.expandedGroups)) {
      this.expandedGroups[g.group] = true;
    }
  });
}


  ngOnDestroy() {
    if (this.valueChangesSub) {
      this.valueChangesSub.unsubscribe();
    }
  }

  onSelectionChange(event: MatSelectChange): void {
    const value = event.value;
    if (this.multiple()) {
      this.selectedValues = value;
      this.selectionChange.emit(this.selectedValues);
      this.formControl.setValue(this.selectedValues, { emitEvent: false });
    } else {
      this.selectedOption = value;
      this.selectionChange.emit(this.selectedOption);
      this.formControl.setValue(this.selectedOption, { emitEvent: false });
    }

    this.onChange(value);
    this.onTouched();
    this.cdr.detectChanges();
  }

  addItem() {
    this.addRecord.emit();
    this.selectedOption = null;
      this.selectionChange.emit(this.selectedOption);
      this.formControl.setValue(this.selectedOption, { emitEvent: false });
  }

  viewItem() {
    event.stopPropagation();
    this.viewRecord.emit(this.selectedOption);
  }

  // ControlValueAccessor implementation
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any): void {
    if (this.multiple()) {
      this.selectedValues = Array.isArray(value) ? value : [];
    } else {
      this.selectedOption = value;
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  toggleFavorite(option: any, event: Event) {
    event.stopPropagation(); // Prevent triggering the dropdown select event
    option.is_favorite = !option.is_favorite;
    this.facorite.emit(option);
  }

  deleteOption(option: any, event: Event): void {
    event.stopPropagation(); // Prevent dropdown from closing
    this.removeSelect.emit(option);
    // this.options = this.options.filter((o) => o.value !== option.value);
  }
}
